import { useState } from "react";
import { Download, Database, Users, Calendar, ShoppingCart, Image, FileText, RefreshCw } from "lucide-react";
import { Button } from "../../components/ui/button";
import { toast } from "sonner";

import { API } from "../../config/api";

export default function AdminBackup() {
  const [downloading, setDownloading] = useState(null);
  const [stats, setStats] = useState(null);
  const [loadingStats, setLoadingStats] = useState(false);

  const backupOptions = [
    { id: "members", label: "Members", icon: Users, description: "All registered member data" },
    { id: "orders", label: "Orders", icon: ShoppingCart, description: "All ticket orders and transactions" },
    { id: "events", label: "Events", icon: Calendar, description: "Event configurations and details" },
    { id: "gallery", label: "Gallery", icon: Image, description: "Gallery items and media references" },
    { id: "content", label: "Site Content", icon: FileText, description: "Website content and settings" },
    { id: "all", label: "Full Backup", icon: Database, description: "Complete database backup" },
  ];

  const fetchStats = async () => {
    setLoadingStats(true);
    try {
      const token = localStorage.getItem("admin_token");
      const response = await fetch(`${API}/admin/backup/stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setStats(data);
      }
    } catch (error) {
      console.error("Error fetching stats:", error);
    } finally {
      setLoadingStats(false);
    }
  };

  const handleDownload = async (type) => {
    setDownloading(type);
    try {
      const token = localStorage.getItem("admin_token");
      const response = await fetch(`${API}/admin/backup/${type}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.ok) {
        const blob = await response.blob();
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `tatvamasi_backup_${type}_${new Date().toISOString().split('T')[0]}.json`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
        toast.success(`${type} backup downloaded successfully`);
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to download backup");
      }
    } catch (error) {
      toast.error("Error downloading backup");
    } finally {
      setDownloading(null);
    }
  };

  return (
    <div className="space-y-6" data-testid="admin-backup">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl text-kerala-green">Data Backup</h1>
          <p className="text-gray-600 text-sm">Download backups of your website data</p>
        </div>
        <Button
          onClick={fetchStats}
          variant="outline"
          disabled={loadingStats}
          className="cursor-pointer"
        >
          <RefreshCw className={`w-4 h-4 mr-2 ${loadingStats ? 'animate-spin' : ''}`} />
          Refresh Stats
        </Button>
      </div>

      {/* Stats Overview */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="bg-white rounded-lg p-4 shadow-sm border border-kerala-gold/10">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-kerala-green/10 rounded-full flex items-center justify-center">
                <Users className="w-5 h-5 text-kerala-green" />
              </div>
              <div>
                <p className="text-2xl font-bold text-kerala-green">{stats.members || 0}</p>
                <p className="text-xs text-gray-500">Members</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-lg p-4 shadow-sm border border-kerala-gold/10">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-kerala-green/10 rounded-full flex items-center justify-center">
                <ShoppingCart className="w-5 h-5 text-kerala-green" />
              </div>
              <div>
                <p className="text-2xl font-bold text-kerala-green">{stats.orders || 0}</p>
                <p className="text-xs text-gray-500">Orders</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-lg p-4 shadow-sm border border-kerala-gold/10">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-kerala-green/10 rounded-full flex items-center justify-center">
                <Calendar className="w-5 h-5 text-kerala-green" />
              </div>
              <div>
                <p className="text-2xl font-bold text-kerala-green">{stats.events || 0}</p>
                <p className="text-xs text-gray-500">Events</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-lg p-4 shadow-sm border border-kerala-gold/10">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-kerala-green/10 rounded-full flex items-center justify-center">
                <Image className="w-5 h-5 text-kerala-green" />
              </div>
              <div>
                <p className="text-2xl font-bold text-kerala-green">{stats.gallery || 0}</p>
                <p className="text-xs text-gray-500">Gallery Items</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Backup Options */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {backupOptions.map((option) => (
          <div
            key={option.id}
            className="bg-white rounded-lg p-6 shadow-sm border border-kerala-gold/10 hover:shadow-md transition-shadow"
          >
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 bg-kerala-green/10 rounded-lg flex items-center justify-center shrink-0">
                <option.icon className="w-6 h-6 text-kerala-green" />
              </div>
              <div className="flex-1">
                <h3 className="font-medium text-kerala-green mb-1">{option.label}</h3>
                <p className="text-sm text-gray-500 mb-4">{option.description}</p>
                <Button
                  onClick={() => handleDownload(option.id)}
                  disabled={downloading === option.id}
                  variant={option.id === "all" ? "default" : "outline"}
                  className={`w-full cursor-pointer ${option.id === "all" ? "btn-primary" : ""}`}
                  data-testid={`backup-${option.id}-btn`}
                >
                  {downloading === option.id ? (
                    <>
                      <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                      Downloading...
                    </>
                  ) : (
                    <>
                      <Download className="w-4 h-4 mr-2" />
                      Download
                    </>
                  )}
                </Button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Info Box */}
      <div className="bg-kerala-cream rounded-lg p-6 border border-kerala-gold/20">
        <h3 className="font-medium text-kerala-green mb-2">Backup Information</h3>
        <ul className="text-sm text-gray-600 space-y-2">
          <li>• Backups are downloaded as JSON files</li>
          <li>• Full backup includes all collections (members, orders, events, gallery, content)</li>
          <li>• Passwords are not included in member backups for security</li>
          <li>• We recommend taking regular backups, especially before major changes</li>
          <li>• Store backups securely - they contain sensitive member information</li>
        </ul>
      </div>
    </div>
  );
}
